import type { ApiClient } from '../client'
import type {
  CloseCaseRequest,
  CreateRecoveryCaseRequest,
  ListRecoveryCasesQuery,
  ManualBookRequest,
  ManualBookResponse,
  RecoveryCandidate,
  RecoveryCase,
  RecoveryCaseDetail,
} from '../types'

/**
 * src/modules/recovery/cases/controllers/recovery-cases.controller.ts — nine routes.
 *
 * ★ `manualBook` is the only call in the app that carries an `Idempotency-Key`: §9 requires one on
 *   booking mutations, and the caller owns the key so a retry reuses it rather than double-booking.
 */
export function recoveryEndpoints(api: ApiClient) {
  return {
    list: (query: ListRecoveryCasesQuery = {}) =>
      api.getPage<RecoveryCase>('recovery-cases', { query: { ...query } }),
    get: (id: string) => api.get<RecoveryCaseDetail>(`recovery-cases/${id}`),
    /** `{ data: { items } }` — the same rows as `detail.candidates`, fetched on their own. */
    candidates: (id: string) => api.getItems<RecoveryCandidate>(`recovery-cases/${id}/candidates`),
    create: (body: CreateRecoveryCaseRequest) => api.post<RecoveryCase>('recovery-cases', body),
    manualBook: (id: string, body: ManualBookRequest, idempotencyKey: string) =>
      api.post<ManualBookResponse>(`recovery-cases/${id}/manual-book`, body, { idempotencyKey }),
    offer: (id: string, candidateId: string) =>
      api.post<RecoveryCandidate>(`recovery-cases/${id}/candidates/${candidateId}/offer`),
    close: (id: string, body: CloseCaseRequest) =>
      api.post<RecoveryCase>(`recovery-cases/${id}/close`, body),
    flagReview: (id: string) => api.post<RecoveryCase>(`recovery-cases/${id}/flag-review`),
    clearReview: (id: string) => api.post<RecoveryCase>(`recovery-cases/${id}/clear-review`),
    /** 202 — the worker re-runs `fill-open-slot`; nothing is booked by this call. */
    refill: (id: string) => api.post<{ readonly enqueued: boolean }>(`recovery-cases/${id}/refill`),
  }
}
